// REACT
import { useContext, useEffect, useState } from "react";
import Select from "react-select";

// ASSETS
import { Button, Form, Alert, Spinner } from "react-bootstrap";
import "../../assets/styles/export.css";
import { FaDownload } from "react-icons/fa";
import languagePack from "../../assets/languages/languagePack";

// CUSTOM HOOKS
import { useDownload, useRead } from "../../hooks/useFetch";

// CONTEXT
import { maincontext } from "../../context/maincontext";
import Loading from "../common/Loading";

export default function Export() {
    const { language, token } = useContext(maincontext)

    // DATES INPUT VARS
    const [startDate, setStartDate] = useState("");
    const [endDate, setEndDate] = useState("");

    // CONFLICTS RENDER
    const [conflictsCountRender, setConflictsCountRender] = useState(null)

    // DISTRIBUTORS INPUT VARS
    const [distributorSelectOptions, setDistributorSelectOptions] = useState([]);
    const [selectedDistributors, setSelectedDistributors] = useState([]);

    // BRANDS INPUT VARS
    const [brandSelectOptions, setBrandSelectOptions] = useState([]);
    const [selectedBrands, setSelectedBrands] = useState([]);

    // PRODUCTS INPUT VARS
    const [productSelectOptions, setProductSelectOptions] = useState([]);
    const [selectedProducts, setSelectedProducts] = useState([]);

    // ERROR VARS
    const [errors, setErrors] = useState({});

    // DOWNLOAD
    const [dataPackage, setDataPackage] = useState(null);
    const exportUrl = `${process.env.REACT_APP_BACKEND_BASE_URL}export`;
    const { isPending, error } = useDownload(dataPackage, exportUrl, `export_${startDate}_${endDate}.xlsx`);

    // FETCH CONFLICTS COUNT
    const conflictsCountUrl = `${process.env.REACT_APP_BACKEND_BASE_URL}conflicts_count`;
    const conflictsCount = useRead(conflictsCountUrl, null);

    // FETCH DISTRIBUTORS
    const distributorOptionsUrl = `${process.env.REACT_APP_BACKEND_BASE_URL}distributors`
    const distributorOptions = useRead(distributorOptionsUrl, null)

    // FETCH BRANDS
    const brandOptionsUrl = `${process.env.REACT_APP_BACKEND_BASE_URL}product_maingroups`
    const brandOptions = useRead(brandOptionsUrl, null)

    // FETCH PRODUCTS
    const productOptionsUrl = `${process.env.REACT_APP_BACKEND_BASE_URL}products`
    const productOptions = useRead(productOptionsUrl, null)


    // CONFLICTS COUNT
    useEffect(() => {
        if (!conflictsCount.isPending && conflictsCount.data) {
            setConflictsCountRender(conflictsCount.data.count)
        }
        else if (conflictsCount.error) {
            setConflictsCountRender(0)
        }
    }, [conflictsCount.data, conflictsCount.error, conflictsCount.isPending])

    // DISTRIBUTORS
    useEffect(() => {
        if (!distributorOptions.isPending && distributorOptions.data) {
            setDistributorSelectOptions((distributorOptions.data).map(distributor => ({
                value: distributor,
                label: distributor
            })))
        }
    }, [distributorOptions.isPending, distributorOptions.error, distributorOptions.data])

    // BRANDS
    useEffect(() => {
        if (!brandOptions.isPending && brandOptions.data) {
            setBrandSelectOptions((brandOptions.data).map(brand => ({
                value: brand,
                label: brand
            })))
        }
    }, [brandOptions.isPending, brandOptions.error, brandOptions.data])

    // PRODUCTS
    useEffect(() => {
        if (!productOptions.isPending && productOptions.data) {
            setProductSelectOptions((productOptions.data).map(product => ({
                value: product.id,
                label: product.product_name
            })))
        }
    }, [productOptions.isPending, productOptions.error, productOptions.data])

    function handleExport() {
        const validationErrors = {};
        const today = new Date().toISOString().split("T")[0];

        if (!startDate) validationErrors.startDate = `${languagePack[language].exportManager.requiredStart}`;
        if (!endDate) validationErrors.endDate = `${languagePack[language].exportManager.requiredEnd}`;
        if (endDate > today) validationErrors.endDate = `${languagePack[language].exportManager.noFutureEndDate}`;

        // ✅ Check max interval of 31 days
        if (startDate && endDate) {
            const start = new Date(startDate);
            const end = new Date(endDate);
            const diffInDays = (end - start) / (1000 * 60 * 60 * 24);
            if (diffInDays > 31) {
                validationErrors.endDate = languagePack[language].exportManager.max31Days || "Date range cannot exceed 31 days.";
            }
            if (diffInDays < 0) {
                validationErrors.endDate = languagePack[language].exportManager.invalidDateRange || "End date must be after start date.";
            }
        }

        if (Object.keys(validationErrors).length > 0) {
            setErrors(validationErrors);
            return;
        }

        setErrors({});
        const requestData = {
            timeframe: { start_date: startDate, end_date: endDate },
        };
        if (selectedDistributors.length > 0) {
            requestData.distributors = selectedDistributors.map(d => d.value);
        }
        if (selectedBrands.length > 0) {
            requestData.brands = selectedBrands.map(b => b.value);
        }
        if (selectedProducts.length > 0) {
            requestData.products = selectedProducts.map(p => p.value);
        }

        setDataPackage(requestData);
    }


    return (
        <>
            {token === null ||
                conflictsCount.isPending ||
                brandOptions.isPending ||
                productOptions.isPending ||
                distributorOptions.isPending ?
                <Loading /> :
                <div className="export-container">
                    <h3>{languagePack[language].exportManager.formTitle}</h3>
                    {(conflictsCountRender !== null &&
                        conflictsCountRender !== undefined &&
                        conflictsCountRender !== 0) &&
                        <h4>{languagePack[language].exportManager.conflictsWarning}
                            <br />
                            {conflictsCountRender}
                        </h4>
                    }
                    <Form>
                        {/* FROM DATE */}
                        <Form.Group className="mb-3">
                            <Form.Label>
                                {languagePack[language].exportManager.startDate}
                                <span className="required">*</span>
                            </Form.Label>
                            <Form.Control
                                type="date"
                                value={startDate}
                                onChange={(e) => setStartDate(e.target.value)}
                            />
                            {errors.startDate && <Alert variant="danger" className="mt-2">{errors.startDate}</Alert>}
                        </Form.Group>

                        {/* UNTIL DATE */}
                        <Form.Group className="mb-3">
                            <Form.Label>
                                {languagePack[language].exportManager.endDate}
                                <span className="required">*</span>
                            </Form.Label>
                            <Form.Control
                                type="date"
                                value={endDate}
                                onChange={(e) => setEndDate(e.target.value)}
                            />
                            {errors.endDate && <Alert variant="danger" className="mt-2">{errors.endDate}</Alert>}
                        </Form.Group>

                        {/* DISTRIBUTORS */}
                        <Form.Group className="mb-3">
                            <Form.Label>{languagePack[language].exportManager.distributors}</Form.Label>
                            <div className="react-select-wrapper">
                                <Select
                                    isMulti
                                    options={distributorSelectOptions}
                                    value={selectedDistributors}
                                    onChange={(selected) => setSelectedDistributors(selected)}
                                    placeholder={languagePack[language].exportManager.distributorsPlaceholder}
                                    classNamePrefix="react-select"
                                />
                            </div>
                        </Form.Group>

                        {/* BRANDS */}
                        <Form.Group className="mb-3">
                            <Form.Label>{languagePack[language].exportManager.brands}</Form.Label>
                            <div className="react-select-wrapper">
                                <Select
                                    isMulti
                                    options={brandSelectOptions}
                                    value={selectedBrands}
                                    onChange={(selected) => setSelectedBrands(selected)}
                                    placeholder={languagePack[language].exportManager.brandsPlaceholder}
                                    classNamePrefix="react-select"
                                />
                            </div>
                        </Form.Group>

                        {/* PRODUCTS */}
                        <Form.Group className="mb-3">
                            <Form.Label>{languagePack[language].exportManager.products}</Form.Label>
                            <div className="react-select-wrapper">
                                <Select
                                    isMulti
                                    options={productSelectOptions}
                                    value={selectedProducts}
                                    onChange={(selected) => setSelectedProducts(selected)}
                                    placeholder={languagePack[language].exportManager.productsPlaceholder}
                                    classNamePrefix="react-select"
                                />
                            </div>
                        </Form.Group>

                        {/* ERROR DISPLAY */}
                        {error && (
                            <Alert variant="danger" className="mt-3">
                                {error.includes("No data found matching the specified filters")
                                    ? languagePack[language].exportManager.noDataFound
                                    : error}
                            </Alert>
                        )}

                        {/* SUBMIT BUTTON */}
                        <div className="text-center">
                            <Button
                                variant="success"
                                onClick={handleExport}
                                disabled={isPending}
                            >
                                {isPending ? (
                                    <>
                                        <Spinner animation="border" size="sm" className="loadingAnimation" />
                                        &nbsp;{languagePack[language].exportManager.downloading}
                                    </>
                                ) : (
                                    <div className="uploadButtonText">
                                        <FaDownload className="text-white downloadIcon" />
                                        &nbsp;{languagePack[language].exportManager.exportBtn}
                                    </div>
                                )}
                            </Button>
                        </div>
                    </Form>
                </div>
            }
        </>
    );
}
